import { Checkbox, Form, Input, InputNumber, message, Modal, Popover, Slider, Spin, Steps, Typography } from "antd";
import { InfoCircleOutlined } from "@ant-design/icons";
import { useEffect, useState } from "react";
import { useSelector } from "react-redux";
import { useTransactionHandlers } from "bc-client/useTransactionHandlers";
import { createClaimAction } from "../../constants/actionNames";
import SourcingDeclaration from "./SourcingDeclaration";
import { checkFeature } from "lib/featureCheck";
import { useUpdateEntitlementCreditBundlesMutation } from "store/is/entitlementCreditBundleApi";

export default function RetireCredit({
  isClaimModalVisible,
  setIsClaimModalVisible,
  handleClaimCancel,
  selectedRows,
  yieldDetails,
  transferAmount,
  setIsAllocateModalVisible,
  handleRealizedCO2eChange
}) {
  const { onAccept: onAcceptcreateClaimAction } = useTransactionHandlers({
    actionname: createClaimAction.name,
  });
  const createClaimActionInBC = async (payload) => {
    return await onAcceptcreateClaimAction(payload);
  };
  const [ updateBundles ] = useUpdateEntitlementCreditBundlesMutation();

  const [claimForm] = Form.useForm();
  const [isSpinning, setIsSpinning] = useState(false);
  const [isBeneficiarySelected, setIsBeneficiarySelected] = useState(false);
  const [retirementPurpose, setRetirementPurpose] = useState("");
  const [claimedRanges, setClaimedRanges] = useState([]);
  const [rangeModalVisible, setRangeModalVisible] = useState(false);
  const [totalCredits, setTotalCredits] = useState(null);
  const [retireModalStep, setRetireModalStep] = useState(0);
  const [sourcingFeatureEnabled, setSourcingFeatureEnabled] = useState(false);
  const [sourcingDeclared, setSourcingDeclared] = useState(false);

  const lmuType = 'land_management_unit';
  const yieldsRequiringSourcing = yieldDetails.filter(yieldDetail => yieldDetail.asset_type === lmuType && !yieldDetail.sourcing_declared_at);
  const sourcingRequired = sourcingFeatureEnabled && yieldsRequiringSourcing.length > 0;
  const showSourcingDeclaration = sourcingRequired && retireModalStep === 0;

  const key = "updatable";
  const userOrgName = useSelector((state) => state.auth?.org?.name);

  let usergroup = useSelector((state) =>
    state.auth.groups?.map((group) => group?.name)
  );

  useEffect(() => {
    setIsSpinning(true)
    const uniqueProjectIds = [ ...new Set(yieldDetails.map(yieldDetail => yieldDetail.project_id_readable)) ].filter(Boolean);
    Promise.all(
      uniqueProjectIds.map(projectId => checkFeature("certificates:insetting-sourcing", projectId))
    ).then(flags => {
      setSourcingFeatureEnabled(flags.some(Boolean))
      setIsSpinning(false);
    });
  }, [userOrgName, yieldDetails])

  const handleClaimAction = async (values) => {
    try {
      setIsSpinning(true);
      claimForm.resetFields();
      if (
        !(
          usergroup.includes("Project Developer") ||
          usergroup.includes("Project Sponsor") ||
          usergroup.includes("Certificate Access")
        )
      ) {
        message.error({
          content: "Action Prohibited",
          key,
          duration: 6,
        });
      } else {
        const updateSelectedRows = () => {
          return selectedRows.map((selectedRow) => {
            const matchingYields = yieldDetails.filter(
              (yieldDetail) =>
                yieldDetail.finaltoken_id === selectedRow.finaltoken_id &&
                yieldDetail.finaltoken_name === selectedRow.finaltoken_name
            );

            const sumOfCredits = matchingYields.reduce((sum, yieldDetail) => {
              return (
                sum +
                (yieldDetail.updated_no_of_credits !== undefined
                  ? yieldDetail.updated_no_of_credits
                  : yieldDetail?.no_of_credits)
              );
            }, 0);

            const updatedNoOfToken = selectedRow.no_of_token - sumOfCredits;

            return {
              ...selectedRow,
              updated_no_of_token: updatedNoOfToken,
            };
          });
        };

        if (
          !(await createClaimActionInBC({
            finalCertificate: updateSelectedRows(),
            claimAmount: transferAmount,
            yieldDetails: yieldDetails,
            retirementPurpose: values?.retirementPurpose,
            beneficiaryName: values?.beneficiary_name,
          }))
        )
          return;
      }
    } catch {
    } finally {
      setIsSpinning(false);
      setIsClaimModalVisible(false);
      setIsAllocateModalVisible(false);
    }
  };

  const handleCheckboxChange = (e) => {
    setIsBeneficiarySelected(e.target.checked);
    if (!e.target.checked) {
      claimForm.setFieldValue("beneficiary_name", "");
      claimForm.setFieldValue(
        "retirementPurpose",
        `Retired on behalf of ${userOrgName}`
      );
    }
  };

  const handleBeneficiaryNameChange = (e) => {
    claimForm.setFieldValue(
      "retirementPurpose",
      `Retired by ${userOrgName} on behalf of ${e.target.value}`
    );
  };

  const closeRangeModal = () => {
    setRangeModalVisible(false);
  };

  const openRangeModal = (claimedRange, noOfToken) => {
    setRangeModalVisible(true);
    const claimedToken =
      claimedRange?.length > 0
        ? claimedRange[claimedRange?.length - 1][1]
        : "0.0000";
    const totalCredits = Number(noOfToken) + Number(claimedToken);

    setClaimedRanges(claimedRange);
    setTotalCredits(totalCredits);

    const newSliderMarks = {
      0: "0"
    };

    if (claimedRange?.length > 0) {
      const formatNumber = (value) => {
        const number = parseFloat(value);
        return Number.isInteger(number) ? number.toString() : number.toFixed(4);
      };

      newSliderMarks[formatNumber(claimedRange[0][0])] = formatNumber(
        claimedRange[0][0]
      );
      newSliderMarks[
        formatNumber(claimedRange[claimedRange.length - 1][1])
      ] = formatNumber(claimedRange[claimedRange.length - 1][1]);
    }
  };

  const handleCancel = () => {
    setRetireModalStep(0);
    handleClaimCancel();
  };

  const retireCreditHeading = <RetireCreditHeading
    retireModalStep={retireModalStep} sourcingRequired={sourcingRequired}
  />

  const handleSourcingDeclarationSubmit = async () => {
    const body = yieldsRequiringSourcing.map(record => ({
      uuid: record._id,
      no_of_credits: record.no_of_credits,
      sourcing_declared_at: true
    }))
    setIsSpinning(true);
    await updateBundles(body);
    setRetireModalStep(1);
    setIsSpinning(false);
  }

  const handleRetireModalSubmit = () => {
    claimForm
      .validateFields()
      .then(async (values) => {
        if (!isBeneficiarySelected) {
          values.beneficiary_name = userOrgName;
        }
        claimForm.resetFields();
        setIsBeneficiarySelected(false);
        handleClaimAction(values);
      })
      .catch((info) => {
        console.log("Validate Failed:", info);
      });
  }

  const handleSubmit = () => {
    if (showSourcingDeclaration) {
      handleSourcingDeclarationSubmit();
    } else {
      handleRetireModalSubmit();
    }
  }

  return (
    <>
      <Modal
        title={retireCreditHeading}
        open={isClaimModalVisible}
        onCancel={handleCancel}
        width={1000}
        onOk={handleSubmit}
        okButtonProps={{ disabled: showSourcingDeclaration ? !sourcingDeclared : transferAmount === 0 }}
      >
        <Spin spinning={isSpinning}>
          { showSourcingDeclaration ?
            <SourcingDeclaration
              yieldsRequiringSourcing={yieldsRequiringSourcing}
              onDeclarationChange={setSourcingDeclared}
            /> : 
            (
              <Form form={claimForm}>
                <Form.Item name="beneficiary_checkbox_selected">
                  <Checkbox onChange={handleCheckboxChange}>
                    Are you retiring credits for a different beneficiary?
                  </Checkbox>
                </Form.Item>
                {isBeneficiarySelected && (
                  <Form.Item
                    name="beneficiary_name"
                    label="Beneficiary Name"
                    labelCol={{ span: 24 }}
                    wrapperCol={{ span: 24 }}
                    style={{ width: "40%" }}
                    rules={[
                      {
                        required: isBeneficiarySelected,
                        message: "Beneficiary name is required",
                      },
                    ]}
                  >
                    <Input
                      placeholder="Please enter the beneficiary name"
                      onChange={handleBeneficiaryNameChange}
                    />
                  </Form.Item>
                )}
                <Form.Item
                  name="retirementPurpose"
                  label="Retirement Purpose"
                  initialValue={`Retired on behalf of ${userOrgName}`}
                  labelCol={{ span: 24 }}
                  wrapperCol={{ span: 24 }}
                >
                  <Input.TextArea
                    rows={4}
                    autoSize={{
                      minRows: 4,
                      maxRows: 5,
                    }}
                    placeholder="Retirement Purpose..."
                    style={{ marginBottom: "16px" }}
                    value={retirementPurpose}
                    onChange={(e) => setRetirementPurpose(e.target.value)}
                  />
                </Form.Item>
                {/* Header */}
                <div
                  style={{
                    display: "grid",
                    gridTemplateColumns: "2fr 2fr 1fr 1fr 1fr 1fr 1fr", // First two columns wider
                    gap: "16px", // Adjust column spacing
                    alignItems: "center",
                    padding: "10px 15px",
                    marginBottom: "16px",
                    backgroundColor: "#f0f0f0", // Background color for header
                    border: "1px solid #ccc", // Border for consistency
                    borderRadius: "4px",
                    fontWeight: "bold", // Bold text for header
                    fontSize: "14px", // Consistent font size
                  }}
                >
                  <div>Credit Bundle ID</div>
                  <div>Project Name</div>
                  <div>Asset Name</div>
                  <div>Period</div>
                  <div>Token ID</div>
                  <div>Credits</div>
                  <div>Range</div>
                </div>

                {/* Content */}
                <div
                  style={{
                    maxHeight: "350px",
                    overflowY: "auto",
                    border: "1px solid #ccc",
                    padding: "10px",
                    borderRadius: "4px",
                    marginBottom: "15px",
                  }}
                >
                  {yieldDetails.map((detail, index) => (
                    <div
                      key={index}
                      style={{
                        display: "grid",
                        gridTemplateColumns: "2fr 2fr 1fr 1fr 1fr 1fr 1fr", // First two columns wider
                        gap: "16px", // Adjust column spacing to match header
                        alignItems: "center",
                        marginBottom:
                          index === yieldDetails.length - 1 ? "0" : "16px",
                        border: "1px solid #ccc",
                        borderRadius: "4px",
                        padding: "10px 15px",
                        backgroundColor: "#f9f9f9", // Light background for content
                        fontSize: "14px", // Ensure the font size matches the header
                      }}
                    >
                      <div>{`${detail?.finaltoken_name} - ${detail?.finaltoken_id}`}</div>
                      <div>{`${detail?.intervention_name}`}</div>
                      <div>{`${detail?.asset_name}`}</div>
                      <div>{`${detail.yield_period}`}</div>
                      <div>{`${detail?.yield_token_name} - ${detail?.yield_token_id}`}</div>
                      <div>
                        <InputNumber
                          min={0.0}
                          step={1}
                          value={
                            detail?.updated_no_of_credits !== undefined
                              ? detail?.updated_no_of_credits
                              : detail?.no_of_credits
                          }
                          onChange={(value) =>
                            handleRealizedCO2eChange(index, value)
                          }
                          max={detail?.no_of_credits}
                          precision={4}
                          formatter={(value) => {
                            return value
                              ? parseFloat(value).toFixed(
                                Math.min(
                                  4,
                                  (value.toString().split(".")[1] || "").length
                                )
                              )
                              : "0";
                          }}
                        />
                      </div>
                      <div style={{ display: "flex", alignItems: "center" }}>
                        <Input
                          value={`${detail?.final_token_claimed_range?.length > 0
                            ? `0 - ${parseFloat(
                              detail?.final_token_claimed_range[
                              detail?.final_token_claimed_range.length - 1
                              ][1]
                            )}`
                            : ""
                            }`}
                          disabled
                          style={{ marginRight: "8px", width: "100%" }}
                        />
                        <InfoCircleOutlined
                          onClick={() =>
                            openRangeModal(
                              detail?.final_token_claimed_range,
                              detail?.no_of_token
                            )
                          }
                          style={{ cursor: "pointer" }}
                        />
                      </div>
                    </div>
                  ))}
                </div>

                <Form.Item label="Amount of credits to be retired">
                  <InputNumber value={transferAmount} precision={4} disabled />
                </Form.Item>

                {transferAmount === 0 && (
                  <div style={{ color: "red", marginBottom: "4px" }}>
                    Amount of credits to be retired should be greater than zero.
                  </div>
                )}
              </Form>
            )
          }
        </Spin>
      </Modal>
      <Modal
        title="Claimed Range"
        visible={rangeModalVisible}
        onCancel={closeRangeModal}
        footer={null}
      >
        <div style={{ position: "relative" }}>
          <Slider
            min={0}
            step={0.0001}
            range
            max={totalCredits}
            value={[
              0,
              claimedRanges?.length > 0
                ? parseFloat(claimedRanges[claimedRanges?.length - 1][1])
                : 0,
            ]}
            disabled
            marks={null}
          />
          <div style={{ marginTop: "10px" }}>
            <Typography.Text>
              Claimed Ranges:{" "}
              {claimedRanges?.map((range) => `0 - ${range[1]}`).join(", ")}
            </Typography.Text>
          </div>
        </div>
      </Modal>
    </>
  )
}

function RetireCreditHeading({ retireModalStep, sourcingRequired }) {
  return sourcingRequired ?
    (
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginRight: "30px" }}>
        <Steps current={retireModalStep} size="small" style={{ flex: 1 }} items={[
          { title: "Sourcing" },
          { title: "Retire" },
        ]} />
        {retireModalStep === 1 &&
          <Popover content={
            <>
              Here you can retire your credits. Please select a specific
              amount of credits if you don't want to retire the full
              credit bundle.
              <br />
              There will be a retirement purpose derived automatically,
              which can be manually updated.
              <br />
              If you are retiring the credits for another beneficiary,
              indicate this below and add the beneficiary name in the
              textbox.
            </>
          }>
            <InfoCircleOutlined style={{ marginLeft: "10px", color: "rgba(0,0,0,.45)" }} />
          </Popover>
        }
      </div>
    ) : (
      <div style={{ display: "flex", alignItems: "center" }}>
        Retire Credit
        <Popover
          content={
            <>
              Here you can retire your credits. Please select a specific
              amount of credits if you don't want to retire the full
              credit bundle.
              <br />
              There will be a retirement purpose derived automatically,
              which can be manually updated.
              <br />
              If you are retiring the credits for another beneficiary,
              indicate this below and add the beneficiary name in the
              textbox.
            </>
          }
        >
          <span
            className="info-icon"
            style={{
              marginLeft: "5px",
              alignSelf: "end",
            }}
          >
            <InfoCircleOutlined />
          </span>
        </Popover>
      </div>
    )
}